import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ChevronLeft, ChevronRight, Plus, Loader2, Calendar as CalIcon,
  Check, Trash2, Repeat, Bell, X, Mail
} from 'lucide-react';
import { tasksService } from './tasksService';
import { useConfirm } from '../../../components/ui/ConfirmDialog';
import './TaskPlanner.css';

const WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
const RECURRINGS = [
  { value: 'NONE', label: 'None' },
  { value: 'DAILY', label: 'Daily' },
  { value: 'WEEKLY', label: 'Weekly' },
  { value: 'MONTHLY', label: 'Monthly' }
];
const STATUSES = [
  { value: 'TODO', label: 'To do' },
  { value: 'IN_PROGRESS', label: 'In progress' },
  { value: 'DONE', label: 'Done' }
];

const REMINDER_PRESETS = [
  { value: 10, label: '10 min before' },
  { value: 15, label: '15 min before' },
  { value: 20, label: '20 min before' },
  { value: 30, label: '30 min before' },
  { value: 45, label: '45 min before' },
  { value: 60, label: '1 hour before' },
  { value: 0,  label: 'At due time' },
  { value: 'custom', label: 'Custom' }
];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function parseEmails(text) {
  return String(text || '')
    .split(/[\s,;]+/)
    .map((e) => e.trim().toLowerCase())
    .filter((e) => EMAIL_RE.test(e));
}

function reminderLabel(r) {
  if (r.offsetMinutes !== null && r.offsetMinutes !== undefined) {
    if (r.offsetMinutes === 0) return 'At due time';
    if (r.offsetMinutes >= 60 && r.offsetMinutes % 60 === 0) {
      const h = r.offsetMinutes / 60;
      return `${h} hour${h === 1 ? '' : 's'} before`;
    }
    return `${r.offsetMinutes} min before`;
  }
  const d = new Date(r.fireAt);
  return d.toLocaleString('en-NG', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

function startOfDay(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}
function startOfMonth(d) {
  const x = new Date(d.getFullYear(), d.getMonth(), 1);
  x.setHours(0, 0, 0, 0);
  return x;
}
function endOfMonth(d) {
  const x = new Date(d.getFullYear(), d.getMonth() + 1, 0);
  x.setHours(23, 59, 59, 999);
  return x;
}
function isSameDay(a, b) {
  return a.getFullYear() === b.getFullYear()
    && a.getMonth() === b.getMonth()
    && a.getDate() === b.getDate();
}
function formatMonthYear(d) {
  return d.toLocaleDateString('en-NG', { month: 'long', year: 'numeric' });
}
function formatLongDay(d) {
  return d.toLocaleDateString('en-NG', { weekday: 'long', month: 'long', day: 'numeric' });
}
function formatTime(date) {
  return new Date(date).toLocaleTimeString('en-NG', { hour: 'numeric', minute: '2-digit' });
}
function toDateInputValue(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}
function toTimeInputValue(d) {
  const h = String(d.getHours()).padStart(2, '0');
  const m = String(d.getMinutes()).padStart(2, '0');
  return `${h}:${m}`;
}

function buildMonthGrid(anchor) {
  const first = startOfMonth(anchor);
  endOfMonth(anchor); // touch for side-effect-free correctness
  const offset = first.getDay();
  const startCell = new Date(first);
  startCell.setDate(first.getDate() - offset);

  const cells = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(startCell);
    d.setDate(startCell.getDate() + i);
    cells.push({
      date: d,
      isCurrentMonth: d.getMonth() === anchor.getMonth(),
      isToday: isSameDay(d, new Date())
    });
  }
  return { cells };
}

/**
 * TaskPlanner — calendar + day's tasks + inline editor.
 *
 * Props
 *   compact   — denser modal layout
 *   splitView — calendar on the left, day's tasks OR editor on the right
 *               (used by the dedicated /utilities/tasks page; modals
 *               stay single-pane because they're too narrow to split)
 *   onViewChange — callback ({ view })
 */
const TaskPlanner = ({ compact = false, splitView = false, onViewChange }) => {
  const { confirm } = useConfirm();
  const [view, setView] = useState('planner'); // 'planner' | 'editor'
  const [anchor, setAnchor] = useState(() => startOfDay(new Date()));
  const [selectedDay, setSelectedDay] = useState(() => startOfDay(new Date()));
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(null);

  useEffect(() => { onViewChange?.({ view }); }, [view, onViewChange]);

  const { cells } = useMemo(() => buildMonthGrid(anchor), [anchor]);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const from = new Date(anchor.getFullYear(), anchor.getMonth(), -6);
      const to = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 14);
      const data = await tasksService.list({ from: from.toISOString(), to: to.toISOString() });
      setTasks(data);
    } catch (err) {
      console.error('[Tasks] list failed', err);
      setError('Could not load tasks.');
    } finally {
      setLoading(false);
    }
  }, [anchor]);

  useEffect(() => { refresh(); }, [refresh]);

  const tasksByDay = useMemo(() => {
    const map = new Map();
    for (const t of tasks) {
      const key = toDateInputValue(new Date(t.dueAt));
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(t);
    }
    return map;
  }, [tasks]);

  const dayTasks = useMemo(() => {
    const key = toDateInputValue(selectedDay);
    return (tasksByDay.get(key) || []).slice().sort((a, b) =>
      new Date(a.dueAt) - new Date(b.dueAt)
    );
  }, [tasksByDay, selectedDay]);

  const startNew = () => {
    const draft = {
      id: null,
      title: '',
      details: '',
      dueAt: new Date(
        selectedDay.getFullYear(), selectedDay.getMonth(), selectedDay.getDate(),
        9, 0, 0, 0
      ),
      allDay: false,
      status: 'TODO',
      recurring: 'NONE',
      reminders: []
    };
    setEditing(draft);
    setView('editor');
  };

  const startEdit = (task) => {
    setEditing({
      ...task,
      dueAt: new Date(task.dueAt),
      reminders: task.reminders || []
    });
    setView('editor');
  };

  const closeEditor = () => {
    setEditing(null);
    setView('planner');
  };

  const handleSave = async () => {
    if (!editing) return;
    const title = (editing.title || '').trim();
    if (!title) { setError('Title is required.'); return; }
    try {
      const payload = {
        title,
        details: editing.details || null,
        dueAt: editing.dueAt.toISOString(),
        allDay: !!editing.allDay,
        status: editing.status,
        recurring: editing.recurring
      };
      if (!editing.id && Array.isArray(editing.reminders) && editing.reminders.length > 0) {
        payload.reminders = editing.reminders.map((r) => ({
          offsetMinutes: r.offsetMinutes ?? null,
          fireAt: r.offsetMinutes === null || r.offsetMinutes === undefined
            ? new Date(r.fireAt).toISOString()
            : undefined,
          recipients: r.recipients
        }));
      }
      const saved = editing.id
        ? await tasksService.update(editing.id, payload)
        : await tasksService.create(payload);

      setTasks((prev) => {
        const idx = prev.findIndex((t) => t.id === saved.id);
        if (idx === -1) return [...prev, saved];
        const next = prev.slice();
        next[idx] = saved;
        return next;
      });
      setSelectedDay(startOfDay(new Date(saved.dueAt)));
      setAnchor(startOfMonth(new Date(saved.dueAt)));
      closeEditor();
    } catch (err) {
      console.error('[Tasks] save failed', err);
      setError('Could not save task.');
    }
  };

  const addReminderToEditing = async (draft) => {
    if (!editing) return;
    if (!Array.isArray(draft.recipients) || draft.recipients.length === 0) {
      setError('Add at least one valid recipient email.');
      return;
    }
    if (editing.id) {
      try {
        const payload = {
          offsetMinutes: draft.offsetMinutes,
          fireAt: draft.offsetMinutes === null ? new Date(draft.fireAt).toISOString() : undefined,
          recipients: draft.recipients
        };
        const saved = await tasksService.addReminder(editing.id, payload);
        setEditing((cur) => ({ ...cur, reminders: [...(cur.reminders || []), saved] }));
        setTasks((prev) => prev.map((t) => (t.id === editing.id
          ? { ...t, reminders: [...(t.reminders || []), saved] }
          : t)));
      } catch (err) {
        console.error('[Tasks/Reminders] add failed', err);
        setError('Could not add reminder.');
      }
    } else {
      const tempId = `tmp_${Date.now()}`;
      const fireAt = draft.offsetMinutes === null
        ? new Date(draft.fireAt)
        : (() => {
            const d = new Date(editing.dueAt);
            d.setMinutes(d.getMinutes() - draft.offsetMinutes);
            return d;
          })();
      setEditing((cur) => ({
        ...cur,
        reminders: [
          ...(cur.reminders || []),
          { id: tempId, offsetMinutes: draft.offsetMinutes, fireAt, recipients: draft.recipients, sent: false }
        ]
      }));
    }
  };

  const removeReminderFromEditing = async (reminder) => {
    if (!editing) return;
    if (editing.id && !String(reminder.id).startsWith('tmp_')) {
      try {
        await tasksService.removeReminder(editing.id, reminder.id);
      } catch (err) {
        console.error('[Tasks/Reminders] delete failed', err);
        setError('Could not remove reminder.');
        return;
      }
      setTasks((prev) => prev.map((t) => (t.id === editing.id
        ? { ...t, reminders: (t.reminders || []).filter((r) => r.id !== reminder.id) }
        : t)));
    }
    setEditing((cur) => ({
      ...cur,
      reminders: (cur.reminders || []).filter((r) => r.id !== reminder.id)
    }));
  };

  const handleToggle = async (task) => {
    try {
      const saved = await tasksService.toggle(task.id);
      setTasks((prev) => prev.map((t) => (t.id === saved.id ? saved : t)));
    } catch (err) {
      console.error('[Tasks] toggle failed', err);
    }
  };

  const handleDelete = async (task, fromEditor = false) => {
    const ok = await confirm(
      `“${task.title}” will be permanently removed.`,
      { title: 'Delete this task?', confirmText: 'Delete', cancelText: 'Keep', type: 'danger' }
    );
    if (!ok) return;
    try {
      await tasksService.remove(task.id);
      setTasks((prev) => prev.filter((t) => t.id !== task.id));
      if (fromEditor) closeEditor();
    } catch (err) {
      console.error('[Tasks] delete failed', err);
      setError('Could not delete task.');
    }
  };

  // ── Reusable render blocks ─────────────────────────────────────────
  const monthLabel = formatMonthYear(anchor);
  const dayLabel = formatLongDay(selectedDay);

  const renderTopHeader = () => (
    <header className="rstp__head">
      <div className="rstp__nav">
        <button
          type="button"
          className="rstp__nav-btn"
          onClick={() => setAnchor((a) => new Date(a.getFullYear(), a.getMonth() - 1, 1))}
          aria-label="Previous month"
        >
          <ChevronLeft size={16} aria-hidden="true" />
        </button>
        <span className="rstp__month-label">{monthLabel}</span>
        <button
          type="button"
          className="rstp__nav-btn"
          onClick={() => setAnchor((a) => new Date(a.getFullYear(), a.getMonth() + 1, 1))}
          aria-label="Next month"
        >
          <ChevronRight size={16} aria-hidden="true" />
        </button>
      </div>
      <div className="rstp__head-actions">
        <button
          type="button"
          className="rstp__today-btn"
          onClick={() => {
            const t = startOfDay(new Date());
            setAnchor(startOfMonth(t));
            setSelectedDay(t);
          }}
        >
          Today
        </button>
        <button type="button" className="rstp__new-btn" onClick={startNew}>
          <Plus size={14} aria-hidden="true" />
          <span>New</span>
        </button>
      </div>
    </header>
  );

  const renderCalendar = () => (
    <div className="rstp__grid-wrap">
      <div className="rstp__weekdays" aria-hidden="true">
        {WEEKDAYS.map((w) => <span key={w} className="rstp__weekday">{w}</span>)}
      </div>
      <div className="rstp__grid" role="grid" aria-label="Calendar">
        {cells.map(({ date, isCurrentMonth, isToday }) => {
          const key = toDateInputValue(date);
          const has = tasksByDay.has(key);
          const isSelected = isSameDay(date, selectedDay);
          return (
            <button
              key={key}
              type="button"
              className={[
                'rstp__cell',
                isCurrentMonth ? '' : 'is-outside',
                isToday ? 'is-today' : '',
                isSelected ? 'is-selected' : '',
                has ? 'has-tasks' : ''
              ].filter(Boolean).join(' ')}
              onClick={() => setSelectedDay(startOfDay(date))}
              aria-label={date.toDateString()}
              aria-current={isToday ? 'date' : undefined}
            >
              <span className="rstp__cell-num">{date.getDate()}</span>
              {has && <span className="rstp__cell-dot" aria-hidden="true" />}
            </button>
          );
        })}
      </div>
    </div>
  );

  const renderDayPanel = () => (
    <section className="rstp__day">
      <header className="rstp__day-head">
        <div>
          <h3 className="rstp__day-title">{dayLabel}</h3>
          <p className="rstp__day-sub">{dayTasks.length} {dayTasks.length === 1 ? 'task' : 'tasks'}</p>
        </div>
      </header>

      {loading ? (
        <div className="rstp__loading"><Loader2 size={14} className="rstp__spin" aria-hidden="true" /> Loading…</div>
      ) : dayTasks.length === 0 ? (
        <div className="rstp__empty">
          <span className="rstp__empty-icon" aria-hidden="true"><CalIcon size={18} /></span>
          <p className="rstp__empty-title">No tasks on this day</p>
          <button type="button" className="rstp__empty-cta" onClick={startNew}>
            <Plus size={14} aria-hidden="true" /> New task
          </button>
        </div>
      ) : (
        <ul className="rstp__day-list" role="list">
          {dayTasks.map((t) => {
            const done = t.status === 'DONE';
            return (
              <li key={t.id}>
                <div className={`rstp__task ${done ? 'is-done' : ''}`}>
                  <button
                    type="button"
                    className="rstp__task-check"
                    onClick={() => handleToggle(t)}
                    aria-pressed={done}
                    aria-label={done ? 'Mark as not done' : 'Mark as done'}
                  >
                    {done && <Check size={12} aria-hidden="true" />}
                  </button>
                  <button type="button" className="rstp__task-body" onClick={() => startEdit(t)}>
                    <span className="rstp__task-row">
                      <span className="rstp__task-title">{t.title}</span>
                      <span className="rstp__task-time">
                        {t.allDay ? 'All day' : formatTime(t.dueAt)}
                      </span>
                    </span>
                    <span className="rstp__task-badges">
                      <span className={`rstp__badge rstp__badge--${t.status.toLowerCase()}`}>
                        {t.status === 'TODO' ? 'To do' : t.status === 'DONE' ? 'Done' : 'In progress'}
                      </span>
                      {t.recurring !== 'NONE' && (
                        <span className="rstp__badge rstp__badge--recurring">
                          <Repeat size={10} aria-hidden="true" /> Recurring
                        </span>
                      )}
                    </span>
                    {t.details && <span className="rstp__task-details">{t.details}</span>}
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );

  const renderEditor = () => {
    if (!editing) return null;
    return (
      <div className="rstp__editor">
        <header className="rstp__editor-head">
          {!splitView && (
            <button type="button" className="rstp__back" onClick={closeEditor} aria-label="Back">
              <ChevronLeft size={18} aria-hidden="true" />
            </button>
          )}
          <div className="rstp__editor-meta">
            <span className="rstp__editor-heading">{editing.id ? 'Edit task' : 'New task'}</span>
          </div>
          {editing.id && (
            <button
              type="button"
              className="rstp__editor-delete"
              onClick={() => handleDelete(editing, true)}
              aria-label="Delete"
              title="Delete"
            >
              <Trash2 size={15} aria-hidden="true" />
            </button>
          )}
          <button type="button" className="rstp__editor-save" onClick={handleSave}>
            {editing.id ? 'Save' : 'Create'}
          </button>
          {splitView && (
            <button type="button" className="rstp__editor-close" onClick={closeEditor} aria-label="Close editor">
              <X size={16} aria-hidden="true" />
            </button>
          )}
        </header>

        {error && <div className="rstp__error">{error}</div>}

        <div className="rstp__form">
          <label className="rstp__field">
            <span className="rstp__field-label">Title</span>
            <input
              type="text"
              className="rstp__input"
              placeholder="e.g. Follow up with vendor"
              value={editing.title}
              onChange={(e) => setEditing({ ...editing, title: e.target.value })}
              autoFocus
              maxLength={200}
            />
          </label>

          <div className="rstp__field-row">
            <label className="rstp__field">
              <span className="rstp__field-label">Date</span>
              <input
                type="date"
                className="rstp__input"
                value={toDateInputValue(editing.dueAt)}
                onChange={(e) => {
                  const [y, m, d] = e.target.value.split('-').map(Number);
                  const next = new Date(editing.dueAt);
                  next.setFullYear(y, m - 1, d);
                  setEditing({ ...editing, dueAt: next });
                }}
              />
            </label>
            <label className="rstp__field">
              <span className="rstp__field-label">Time</span>
              <input
                type="time"
                className="rstp__input"
                value={toTimeInputValue(editing.dueAt)}
                disabled={editing.allDay}
                onChange={(e) => {
                  const [h, m] = e.target.value.split(':').map(Number);
                  const next = new Date(editing.dueAt);
                  next.setHours(h, m, 0, 0);
                  setEditing({ ...editing, dueAt: next });
                }}
              />
            </label>
          </div>

          <label className="rstp__inline-toggle">
            <input
              type="checkbox"
              checked={editing.allDay}
              onChange={(e) => setEditing({ ...editing, allDay: e.target.checked })}
            />
            <span>All day</span>
          </label>

          <div className="rstp__field-row">
            <label className="rstp__field">
              <span className="rstp__field-label">Status</span>
              <select
                className="rstp__input"
                value={editing.status}
                onChange={(e) => setEditing({ ...editing, status: e.target.value })}
              >
                {STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </label>
            <label className="rstp__field">
              <span className="rstp__field-label">Recurring</span>
              <select
                className="rstp__input"
                value={editing.recurring}
                onChange={(e) => setEditing({ ...editing, recurring: e.target.value })}
              >
                {RECURRINGS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
            </label>
          </div>

          <label className="rstp__field">
            <span className="rstp__field-label">Details <span className="rstp__field-hint">(optional)</span></span>
            <textarea
              className="rstp__textarea"
              placeholder="What's the context? What's the desired outcome?"
              value={editing.details || ''}
              onChange={(e) => setEditing({ ...editing, details: e.target.value })}
              maxLength={5000}
              rows={4}
            />
          </label>

          <RemindersSection
            reminders={editing.reminders || []}
            dueAt={editing.dueAt}
            onAdd={addReminderToEditing}
            onRemove={removeReminderFromEditing}
          />
        </div>
      </div>
    );
  };

  // ── Compose ────────────────────────────────────────────────────────
  // Split mode (page only): calendar + right pane side-by-side. The
  // right pane swaps between day-tasks and editor. Modal stays
  // single-pane because it's too narrow.
  if (splitView) {
    return (
      <div className={`rstp rstp--split ${compact ? 'rstp--compact' : ''}`}>
        {renderTopHeader()}
        {error && view !== 'editor' && <div className="rstp__error">{error}</div>}
        <div className="rstp__split-body">
          <div className="rstp__split-left">{renderCalendar()}</div>
          <div className="rstp__split-right">
            {view === 'editor' && editing ? renderEditor() : renderDayPanel()}
          </div>
        </div>
      </div>
    );
  }

  // Non-split (modal): toggle entire view between planner and editor.
  if (view === 'editor' && editing) {
    return (
      <div className={`rstp rstp--editor-only ${compact ? 'rstp--compact' : ''}`}>
        {renderEditor()}
      </div>
    );
  }

  return (
    <div className={`rstp ${compact ? 'rstp--compact' : ''}`}>
      {renderTopHeader()}
      {error && <div className="rstp__error">{error}</div>}
      {renderCalendar()}
      {renderDayPanel()}
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────────────
   RemindersSection — preset chips + recipient pills inside the editor.
   ──────────────────────────────────────────────────────────────────── */
function RemindersSection({ reminders, dueAt, onAdd, onRemove }) {
  const [adding, setAdding] = useState(false);
  const [offsetChoice, setOffsetChoice] = useState(30);
  const [customAt, setCustomAt] = useState(() => {
    const d = new Date(dueAt);
    d.setMinutes(d.getMinutes() - 30);
    return d;
  });
  const [recipientsInput, setRecipientsInput] = useState('');
  const [recipientChips, setRecipientChips] = useState([]);
  const [localErr, setLocalErr] = useState(null);

  const isCustom = offsetChoice === 'custom';

  const commitInputToChips = () => {
    const emails = parseEmails(recipientsInput);
    if (emails.length === 0) return;
    setRecipientChips((cs) => Array.from(new Set([...cs, ...emails])));
    setRecipientsInput('');
  };

  const handleRecipientKey = (e) => {
    if (e.key === 'Enter' || e.key === ',' || e.key === ' ' || e.key === 'Tab') {
      if (recipientsInput.trim()) {
        e.preventDefault();
        commitInputToChips();
      }
    } else if (e.key === 'Backspace' && !recipientsInput && recipientChips.length > 0) {
      setRecipientChips((cs) => cs.slice(0, -1));
    }
  };

  const removeChip = (email) => setRecipientChips((cs) => cs.filter((c) => c !== email));

  const resetForm = () => {
    setAdding(false);
    setOffsetChoice(30);
    setRecipientsInput('');
    setRecipientChips([]);
    setLocalErr(null);
  };

  const handleSubmit = async () => {
    let recipients = recipientChips.slice();
    if (recipientsInput.trim()) {
      recipients = Array.from(new Set([...recipients, ...parseEmails(recipientsInput)]));
    }
    if (recipients.length === 0) {
      setLocalErr('Enter at least one valid email address.');
      return;
    }
    setLocalErr(null);

    const draft = { recipients: recipients.map((email) => ({ email })) };

    if (isCustom) {
      const fire = new Date(customAt);
      if (Number.isNaN(fire.getTime())) {
        setLocalErr('Pick a valid date and time.');
        return;
      }
      draft.offsetMinutes = null;
      draft.fireAt = fire;
    } else {
      draft.offsetMinutes = Number(offsetChoice);
    }

    await onAdd(draft);
    resetForm();
  };

  return (
    <div className="rstp__reminders">
      <div className="rstp__reminders-head">
        <span className="rstp__reminders-icon" aria-hidden="true"><Bell size={14} /></span>
        <div className="rstp__reminders-meta">
          <span className="rstp__field-label" style={{ textTransform: 'none', letterSpacing: 0, fontSize: 13, color: '#111827' }}>
            Reminders
          </span>
          <span className="rstp__reminders-sub">
            Email this task to staff or anyone else at a time you pick.
          </span>
        </div>
        {!adding && (
          <button type="button" className="rstp__reminders-add" onClick={() => setAdding(true)}>
            <Plus size={13} aria-hidden="true" /> Add
          </button>
        )}
      </div>

      {reminders.length > 0 && (
        <ul className="rstp__reminders-list" role="list">
          {reminders.map((r) => (
            <li key={r.id} className={`rstp__reminder ${r.sent ? 'is-sent' : ''}`}>
              <span className="rstp__reminder-time">
                <Bell size={11} aria-hidden="true" />
                {reminderLabel(r)}
                {r.sent && <span className="rstp__reminder-sent-badge">Sent</span>}
              </span>
              <span className="rstp__reminder-recipients">
                <Mail size={11} aria-hidden="true" />
                {(r.recipients || []).map((x) => x.email).join(', ')}
              </span>
              <button
                type="button"
                className="rstp__reminder-remove"
                onClick={() => onRemove(r)}
                aria-label="Remove reminder"
                title="Remove"
              >
                <X size={13} aria-hidden="true" />
              </button>
            </li>
          ))}
        </ul>
      )}

      {adding && (
        <div className="rstp__reminder-form">
          <div className="rstp__field">
            <span className="rstp__field-label">When</span>
            {/* Desktop: chip group (faster to scan + tap). Mobile: native
                select so the OS picker handles 8 options without eating
                vertical space. Both bound to the same state. */}
            <div className="rstp__preset-chips" role="radiogroup" aria-label="Reminder time">
              {REMINDER_PRESETS.map((p) => (
                <button
                  key={p.value}
                  type="button"
                  role="radio"
                  aria-checked={offsetChoice === p.value}
                  className={`rstp__chip ${offsetChoice === p.value ? 'is-active' : ''}`}
                  onClick={() => setOffsetChoice(p.value)}
                >
                  {p.label}
                </button>
              ))}
            </div>
            <select
              className="rstp__input rstp__preset-select"
              value={String(offsetChoice)}
              onChange={(e) => {
                const v = e.target.value;
                setOffsetChoice(v === 'custom' ? 'custom' : Number(v));
              }}
              aria-label="Reminder time"
            >
              {REMINDER_PRESETS.map((p) => (
                <option key={p.value} value={String(p.value)}>{p.label}</option>
              ))}
            </select>
          </div>

          {isCustom && (
            <label className="rstp__field">
              <span className="rstp__field-label">Custom date &amp; time</span>
              <input
                type="datetime-local"
                className="rstp__input"
                value={(() => {
                  const d = new Date(customAt);
                  const pad = (n) => String(n).padStart(2, '0');
                  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
                })()}
                onChange={(e) => { if (e.target.value) setCustomAt(new Date(e.target.value)); }}
              />
            </label>
          )}

          <div className="rstp__field">
            <span className="rstp__field-label">Send to <span className="rstp__field-hint">(comma-separated emails)</span></span>
            <div className="rstp__chips-input">
              {recipientChips.map((email) => (
                <span key={email} className="rstp__recipient-chip">
                  {email}
                  <button type="button" onClick={() => removeChip(email)} aria-label={`Remove ${email}`}>
                    <X size={11} aria-hidden="true" />
                  </button>
                </span>
              ))}
              <input
                type="text"
                className="rstp__chips-field"
                placeholder={recipientChips.length === 0 ? 'staff@store.com, you@store.com' : ''}
                value={recipientsInput}
                onChange={(e) => setRecipientsInput(e.target.value)}
                onKeyDown={handleRecipientKey}
                onBlur={commitInputToChips}
              />
            </div>
          </div>

          {localErr && <div className="rstp__error">{localErr}</div>}

          <div className="rstp__reminder-form-actions">
            <button type="button" className="rstp__btn-secondary" onClick={resetForm}>Cancel</button>
            <button type="button" className="rstp__btn-primary" onClick={handleSubmit}>Add reminder</button>
          </div>
        </div>
      )}
    </div>
  );
}

export default TaskPlanner;
